import {
  AccountInfoParamsV2,
  LinkTemplate,
  MessageType,
  Notification,
  PlacementType,
  SearchParams,
  SortMode,
  SortOrder,
  TextTemplate,
  TitleHeaderTemplate,
  useNotifications,
  useNotificationsV2,
} from '@amzn/aces-communications-api';
import { UseAcesBannersMetricsPublisher } from '../../../../commons/utils/metrics';
import { logger } from '../../../../commons/utils/logger';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BANNER_TYPE, selectChromeConfiguration } from '../../../../app/slice';
import {
  LocalNotification,
  SwitcherGlobalAccount,
  UseAcesBannersResponse,
} from './types';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { CONSOLE_NOTIFICATIONS_API_CLIENT_ID } from 'src/commons/constants';

const BANNER_SEARCH_PARAMS = {
  page: { size: 10, current: 0 },
  sort: { sortBy: SortMode?.MESSAGE_TYPE, sortDirection: SortOrder?.DESC },
  filter: {},
};

// Single Global Ad Account ID (SGA) format. Only a value matching this prefix is a valid
// SGA; we never forward a member entity id (or anything else) as the global account id.
const SGA_ID_PATTERN = /^amzn1\.ads-account\.g\./;

// Deliberately partial over MessageType: only these three map to a banner
// BANNER_TYPE; any other MessageType (ALERT/OPPORTUNITY/UPDATE) yields undefined
// and is filtered out below. Enum members keep the emitted string values
// identical ('info'/'warning'/'error').
const SUPPORTED_ACES_MSG_TYPES: Partial<Record<MessageType, BANNER_TYPE>> = {
  INFORMATION: BANNER_TYPE.INFO,
  WARNING: BANNER_TYPE.WARNING,
  ERROR: BANNER_TYPE.ERROR,
};

// Raw actions returned by the aces notifications hooks. Mirrors the banner-facing
// UseAcesBannersResponseActions except dismiss operates on a Notification (not a
// LocalNotification) — mergedActions below bridges that difference.
interface RawAcesActions {
  refresh: () => void;
  display: (toMark?: Notification[]) => Promise<void>;
  read: (toMark: Notification) => Promise<void>;
  readSubNotification: (toMark: string) => Promise<void>;
  dismiss: (toMark: Notification) => Promise<void>;
}

// Return tuple shape shared by useNotifications (v1) and useNotificationsV2 (v2).
type UseNotificationsReturn = readonly [
  Notification[],
  number,
  boolean,
  RawAcesActions,
];

/**
 * Builds the search params passed to the aces notifications hook. Identical for
 * both the v1 (CHFS) and v2 (WonderwallService) paths — the v2 adapter forwards
 * isGlobal/isAggregated to WWS. isGlobal is only honoured for PAGE_BANNER.
 */
const useBannerSearchParams = (placement: PlacementType): SearchParams => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const chromeConfiguration = useSelector(selectChromeConfiguration);
  let isGlobal = false;
  let isAggregated = false;

  if (
    placement === PlacementType.PAGE_BANNER &&
    chromeConfiguration.isAggregated &&
    chromeConfiguration.isGlobal
  ) {
    // isGlobal as true is only supported by PAGE_BANNER, any other placement type will result in an error. Use with caution.
    isGlobal = chromeConfiguration.isGlobal;
    // isAggregated flag only useful on PAGE_BANNER type. Any other types is either ignored or will result in unintended data. Change code with caution.
    isAggregated = chromeConfiguration.isAggregated;
  }

  return useMemo(
    () => ({
      ...BANNER_SEARCH_PARAMS,
      isGlobal,
      isAggregated,
      filter: {
        webpageId: chromeConfiguration.pageId,
        applicationName: internalChromeConfig?.applicationName,
      },
    }),
    [
      chromeConfiguration.pageId,
      internalChromeConfig?.applicationName,
      isGlobal,
      isAggregated,
    ],
  );
};

/**
 * Shared response shaping for both banner paths. Parses the aces v1 Notification
 * model (both paths return this model — the v2 adapter converts WWS responses
 * back into it) into LocalNotification banners and wires up the dismiss action.
 */
const useParsedAcesBanners = ([
  notifications,
  ,
  loading,
  actions,
]: UseNotificationsReturn): UseAcesBannersResponse => {
  const [localNotifications, setLocalNotifications] = useState<
    LocalNotification[]
  >([]);

  const mergedActions = useMemo(
    () => ({
      ...actions,
      dismiss: (bannerObjToUpdate: LocalNotification) => {
        const notificationsToUpdate = notifications?.find(
          (n) => n.eventId === bannerObjToUpdate.id,
        );
        try {
          if (notificationsToUpdate) {
            return actions.dismiss(notificationsToUpdate);
          } else {
            throw new Error('Banner notification not found');
          }
        } catch (error: any) {
          UseAcesBannersMetricsPublisher.publishCounterMonitor(
            `dismissACESBannerFailure:${bannerObjToUpdate.id}`,
            1,
          );
          logger.error(
            `dismiss aces banner failed for: ${bannerObjToUpdate.id}`,
            error,
            { errorInfo: 'aces-dismiss' },
          );
        }
        return Promise.reject();
      },
    }),
    [actions, notifications],
  );

  useEffect(() => {
    if (loading) {
      return;
    }
    // only support INFORMATION, ERROR, WARNING messageType:
    // https://w.amazon.com/bin/view/Advertising_Console/Advertising_Console_Experience/Console_Banners
    // skip other messageTypes

    const parsedNotifications: LocalNotification[] = notifications
      ?.filter((item) => SUPPORTED_ACES_MSG_TYPES[item?.messageType])
      ?.map((item) => {
        const title = item.components?.find(
          (component) => component.type === 'TitleHeader',
        ) as TitleHeaderTemplate;
        // Guaranteed present: this item already passed the
        // SUPPORTED_ACES_MSG_TYPES filter above, so the lookup cannot miss here.
        const messageType = SUPPORTED_ACES_MSG_TYPES[item.messageType]!;
        const acesBannerContent = item.components?.filter(
          (component) => component.type === 'Text' || component.type === 'Link',
        ) as (TextTemplate | LinkTemplate)[];

        return {
          id: item.eventId,
          eventId: item.eventId, // bannerItem shape will have eventId only when data is returned from ACES API
          type: messageType,
          title: title?.text,
          acesBannerContent,
          notificationType: 'aces',
        };
      });

    setLocalNotifications(parsedNotifications);
  }, [notifications, loading]);

  return {
    notifications: localNotifications,
    actions: mergedActions,
    loading: loading,
  };
};

/**
 * Fetches banners via the legacy v1 hook (CHFS via MONS). Default path when the
 * NOTIFICATIONS_WWS_DIRECT weblab is off.
 */
export const useAcesBanners = (
  placement: PlacementType,
): UseAcesBannersResponse => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const searchParams = useBannerSearchParams(placement);

  const accountParams = useMemo(
    () => ({
      entityId: internalChromeConfig?.entityId,
    }),
    [internalChromeConfig?.entityId],
  );

  return useParsedAcesBanners(
    useNotifications(placement, searchParams, accountParams),
  );
};

/**
 * Why a switcher-resolved SGA is withheld from the request. `entity-mismatch` is
 * the switcher and the page naming different member entities. `entity-unresolved`
 * is a valid SGA with no member entity behind it; findCurrentAccountAndSiblings
 * returns an empty SGA whenever it resolves no entity, so this is defensive and
 * counted separately so it never inflates the mismatch population.
 */
type SgaDrop = 'entity-mismatch' | 'entity-unresolved';

const SGA_DROP_METRIC: Record<SgaDrop, string> = {
  'entity-mismatch': 'sgaEntityMismatch',
  'entity-unresolved': 'sgaEntityUnresolved',
};

// The SGA the switcher inferred is only authoritative for the entity the switcher
// resolved. The page renders internalChromeConfig.entityId, which the switcher XHR
// resolves independently (from the session unless the page URL carries entityId=),
// so the two can name different member entities. Forwarding the SGA in that case
// sends WWS an Amazon-Ads-AccountId that does not own the requested
// Amazon-Advertising-API-AdvertiserId, which WWS rejects with a 403.
const selectGlobalAccountId = (
  switcherGlobalAccountId: string | undefined,
  switcherEntityId: string | undefined,
  pageEntityId: string | undefined,
): { globalAccountId?: string; drop?: SgaDrop } => {
  if (
    !switcherGlobalAccountId ||
    !SGA_ID_PATTERN.test(switcherGlobalAccountId)
  ) {
    return {};
  }
  if (!switcherEntityId) {
    return { drop: 'entity-unresolved' };
  }
  if (switcherEntityId !== pageEntityId) {
    return { drop: 'entity-mismatch' };
  }
  return { globalAccountId: switcherGlobalAccountId };
};

/**
 * Fetches banners via the v2 hook, which calls WonderwallService directly
 * through the Advertising API Gateway (/a9g-api-gateway), bypassing CHFS.
 * Enabled by the NOTIFICATIONS_WWS_DIRECT weblab. The extra context fields
 * (clientId, marketplaceId, locale) required by AccountInfoParamsV2 are already
 * available on the chrome config; v2 issues CSRF inline so no token is passed.
 *
 * For global (multi-marketplace) banner requests WWS additionally needs the Single Global
 * Ad Account ID (SGA). It is not on internalChromeConfig, so it is sourced from Chrome via
 * useAccountInfo() (getAccountInfoFromSwitcherInit()/getAccountInfo()) — the same hook the
 * MarketplaceSwitcher uses — and passed in as switcherGlobalAccount. entityId stays the
 * member entity. We only forward globalAccountId when it is a valid SGA
 * (amzn1.ads-account.g.*) and the switcher resolved the same entity the page renders; the
 * aces adapter fails fast on a global request that arrives without one.
 *
 * switcherGlobalAccount is passed in (not read here) so the caller can hold this hook off
 * until useAccountInfo has resolved — useNotificationsV2 refetches whenever accountParams
 * changes, so mounting before the SGA is known would fire a first request without it, then
 * a second once it lands. See AcesBannerSourceV2 in BannerWithAces.tsx.
 */
export const useAcesBannersV2 = (
  placement: PlacementType,
  switcherGlobalAccount?: SwitcherGlobalAccount,
): UseAcesBannersResponse => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const searchParams = useBannerSearchParams(placement);
  const pageEntityId = internalChromeConfig?.entityId;
  const switcherGlobalAccountId = switcherGlobalAccount?.globalAccountId;
  const switcherEntityId = switcherGlobalAccount?.memberEntityId;

  const { globalAccountId, drop } = useMemo(
    () =>
      selectGlobalAccountId(
        switcherGlobalAccountId,
        switcherEntityId,
        pageEntityId,
      ),
    [switcherGlobalAccountId, switcherEntityId, pageEntityId],
  );

  // Emit once per distinct drop, outside render, so StrictMode double-render
  // and unrelated re-renders do not inflate the count.
  useEffect(() => {
    if (!drop) {
      return;
    }
    UseAcesBannersMetricsPublisher.publishCounterMonitor(
      SGA_DROP_METRIC[drop],
      1,
    );
    logger.warn(
      drop === 'entity-mismatch'
        ? `aces banner SGA dropped: switcher resolved ${switcherEntityId} but page is ${pageEntityId}`
        : `aces banner SGA dropped: switcher resolved no member entity for page ${pageEntityId}`,
      { errorInfo: `aces-sga-${drop}`, placement },
    );
  }, [drop, switcherEntityId, pageEntityId, placement]);

  const accountParams = useMemo<AccountInfoParamsV2>(
    () => ({
      entityId: pageEntityId,
      clientId: CONSOLE_NOTIFICATIONS_API_CLIENT_ID,
      marketplaceId: internalChromeConfig?.marketplaceId,
      locale: internalChromeConfig?.locale,
      globalAccountId,
    }),
    [
      pageEntityId,
      internalChromeConfig?.marketplaceId,
      internalChromeConfig?.locale,
      globalAccountId,
    ],
  );

  return useParsedAcesBanners(
    useNotificationsV2(placement, searchParams, accountParams),
  );
};
